/* eslint-disable react-hooks/exhaustive-deps */
import React, { FunctionComponent, useEffect, useMemo, useState } from 'react';
import {
  Button,
  ButtonToolbar,
  ToggleButton,
  ToggleButtonGroup,
} from 'react-bootstrap';
// @ts-ignore @TODO: add types for package
import { Confirm } from 'react-confirm-bootstrap';
import { Typeahead } from 'react-bootstrap-typeahead';
import { cloneDeep, orderBy, uniq, lowerCase, set } from 'lodash';
import { Dimmer, Loader } from 'semantic-ui-react';
import produce from 'immer';
import Modal from 'react-modal';
import { CLIENT_TYPE, Condition } from '@upstart/harmony/lib/constants';
import {
  CarPriceRule,
  PriceRule,
  PriceRulesEntry,
  PriceRulesRevision,
} from '@upstart/harmony/lib/legos/dealerTypes';
import { PriceRules } from '@upstart/harmony/lib/legos/priceRulesTypes';
import { Action } from 'util/amplitudeEvents';
import DecodeService from '../../services/DecodeService';
import DealerService from '../../services/DealerService';
import { DefaultRulesTable, ModelRulesTable } from './RuleTables';
import {
  AllTrimVinFilter,
  DealerForPriceMatrix,
  ModelEntry,
  PriceRulesEntryUpdater,
  UserForPriceMatrix,
} from './types';
import ErrorMessage from '../ErrorMessage';
import { getClientTypeName } from '../../util/constants';
import './PricingMatrixEditor.scss';
import VINSearch from '../VINSearch';
import CloseIcon from '../Icons/Close';
import { getDefaultPriceRulesEntry, getOppositeClientType } from './utils';

const MODAL_STYLE = {
  content: {
    margin: '0 auto',
    maxWidth: '500px',
    position: 'relative',
  },
};

// fix Typeahead's props type
declare module 'react-bootstrap-typeahead' {
  // The unused generic is needed to match the TypeaheadProps interface here
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  interface TypeaheadProps<T> {
    className?: string;
  }
}

/**
 * Generates a consistent rule id from a model entry and trim
 */
function generateRuleId(modelEntry: ModelEntry, trim?: string, vin?: string) {
  return `year-${modelEntry.year}-make-${modelEntry.make}-model-${
    modelEntry.model
  }${trim !== undefined ? `-trim-${trim}` : ''}${
    vin !== undefined ? `-vin-${vin}` : ''
  }`;
}

/**
 * Matches a model entry to a price rule
 *
 * @param modelEntry the model entry to match
 * @param matchTrim whether trim should be considered
 * @param trim the trim to match with (if matchTrim is true)
 * @param matchVin whether VIN should be considered
 * @param vin the VIN to match with (if matchVin is true)
 * @param rule the rule to match
 */
export const matchModelEntry = (
  modelEntry: ModelEntry,
  matchTrim = false,
  trim?: string,
  matchVin = false,
  vin?: string,
) => (rule: CarPriceRule) =>
  rule.year === modelEntry.year &&
  lowerCase(rule.make) === lowerCase(modelEntry.make) &&
  lowerCase(rule.model) === lowerCase(modelEntry.model) &&
  (!matchTrim || lowerCase(rule.trim) === lowerCase(trim)) &&
  (!matchVin || rule.vin === vin);

// Only match rules without a VIN set
export const matchModelEntryWithoutVin = (
  modelEntry: ModelEntry,
  matchTrim = false,
  trim?: string,
) => (rule: CarPriceRule) =>
  matchModelEntry(modelEntry, matchTrim, trim)(rule) && !rule.vin;

interface Props {
  dealerPriceRules?: PriceRules;
  dealer?: DealerForPriceMatrix;
  users: UserForPriceMatrix[];
  clientType: CLIENT_TYPE;
  condition: Condition;
  // filter for years to show, empty will show all
  years?: string[];
  onYearsChanged?: (years: string[]) => void;
  // filter for models to show, empty will show all
  models?: string[];
  onModelsChanged?: (models: string[]) => void;
  // filter to only show rules that are enabled
  onlyShowEnabled?: boolean;
  onOnlyShowEnabledChanged?: (onlyShowEnabled: boolean) => void;
  onChange: (isReset: boolean) => void;
  onSave: (dealerPriceRules: PriceRules) => void;
  fetching: boolean;
  sendAmplitudeEvent: (
    client: CLIENT_TYPE,
    condition: Condition,
    action: Action,
  ) => void;
}

export const PricingMatrixEditor: FunctionComponent<Props> = ({
  dealerPriceRules,
  dealer,
  users,
  clientType,
  condition,
  years: filteredYears,
  onYearsChanged: onFilteredYearsChanged,
  models: filteredModels,
  onModelsChanged: onFilteredModelsChanged,
  onlyShowEnabled,
  onOnlyShowEnabledChanged,
  onChange,
  onSave,
  fetching,
  sendAmplitudeEvent,
}) => {
  const [errorMessage, setErrorMessage] = useState<string | object>();
  const [resetTrigger, setResetTrigger] = useState(0);
  const [showVINAdd, setShowVINAdd] = useState(false);
  const [showVINAddResults, setShowVINAddResults] = useState(false);
  const [vinAddMessage, setVinAddMessage] = useState('');
  const [priceRules, setPriceRules] = useState<PriceRulesEntry>(
    getDefaultPriceRulesEntry(),
  );

  const [dealerCashEnabled, setDealerCashEnabled] = useState(false);
  const [
    selectInventoryRebateEnabled,
    setSelectInventoryRebateEnabled,
  ] = useState(false);
  const [allTrimVinFilter, setAllTrimVinFilter] = useState<AllTrimVinFilter>(
    AllTrimVinFilter.ALL,
  );

  useEffect(() => {
    setDealerCashEnabled(
      !!dealerPriceRules?.data?.[clientType]?.[condition]
        ?.enableDealerCashPriceOffset,
    );
    setSelectInventoryRebateEnabled(
      !!dealerPriceRules?.data?.[clientType]?.[condition]
        ?.enableSelectInventoryRebate,
    );
  }, [dealerPriceRules, clientType, condition]);

  const updateDealerCashEnabled = (enabled: boolean) => {
    setDealerCashEnabled(enabled);
    onChange(false);
  };
  const updateSelectInventoryRebateEnabled = (enabled: boolean) => {
    setSelectInventoryRebateEnabled(enabled);
    onChange(false);
  };

  useEffect(() => {
    sendAmplitudeEvent(clientType, condition, Action.START);
  }, [clientType, condition]);

  const onDealerSaved = () => {
    if (!dealer) return;
    let newDealerPriceRules = cloneDeep(dealerPriceRules);
    newDealerPriceRules = newDealerPriceRules || { id: dealer.id };

    // save the updated pricing rules
    set(newDealerPriceRules, `data.${clientType}.${condition}`, {
      ...priceRules,
      enableDealerCashPriceOffset: dealerCashEnabled,
      enableSelectInventoryRebate: selectInventoryRebateEnabled,
    });
    onSave(newDealerPriceRules);
    sendAmplitudeEvent(clientType, condition, Action.SAVE);
  };

  /**
   * Helper function to update the price rules object.
   * Allows for an immer style object update https://immerjs.github.io/immer/docs/introduction#quick-example
   *
   * @param apply a function that edits a copy of priceRules
   */
  const updatePriceRules: PriceRulesEntryUpdater = apply => {
    // use immer to edit priceRules with the given editor function and save the edited price rules
    setPriceRules(
      produce(newPriceRules => {
        apply(newPriceRules);
      }),
    );
    onChange(false);
  };
  // updates the local price rules state if the dealer changes or a reset is triggered
  useEffect(() => {
    setPriceRules(
      dealerPriceRules?.data?.[clientType]?.[condition] ??
        getDefaultPriceRulesEntry(),
    );
    onChange(true);
  }, [dealerPriceRules, clientType, resetTrigger]);

  const [priceRulesRevisions, setPriceRulesRevisions] = useState<
    PriceRulesRevision[] | undefined
  >();
  // gets the price rule revision data when the dealer is loaded and whenever it's updated
  useEffect(() => {
    if (dealer?.id) {
      DealerService.getPriceRulesRevisions(dealer.id)
        .then(r => setPriceRulesRevisions(orderBy(r, 'updatedAt', 'desc')))
        .catch(() => {
          setErrorMessage(
            'There was an error trying to retrieve pricing history',
          );
        });
    }
  }, [dealer?.id, dealer?.updatedAt]);

  const [allModels, setAllModels] = useState<ModelEntry[] | undefined>();
  // gets all the model data for the dealer's brand
  useEffect(() => {
    if (dealer?.id) {
      if (dealer?.brand && dealer.brand.length > 0) {
        DecodeService.getRecentModels(dealer.brand.join(','))
          .then(v => setAllModels(v))
          .catch(() => {
            setErrorMessage(
              'There was an error trying to get the list of models',
            );
          });
      } else {
        setErrorMessage('Dealer is missing a brand');
      }
    }
  }, [dealer]);

  // applies filters on the model data
  const models = useMemo(() => {
    // year filter
    const modelsByYearFilter =
      (filteredYears?.length || 0) > 0
        ? (allModels || []).filter(m => filteredYears?.includes(`${m.year}`))
        : allModels || [];
    // model filter
    const modelsByModelFilter =
      (filteredModels?.length || 0) > 0
        ? modelsByYearFilter.filter(m => filteredModels?.includes(m.model))
        : modelsByYearFilter;
    // enabled filter
    const modelsByEnabledFilter = onlyShowEnabled
      ? modelsByModelFilter.filter(m =>
          priceRules.carRules?.find(matchModelEntry(m)),
        )
      : modelsByModelFilter;
    return modelsByEnabledFilter;
  }, [priceRules, allModels, filteredYears, filteredModels, onlyShowEnabled]);

  const renderFilterMenu = () => (
    <div className="filter-menu">
      Filter by:
      <Typeahead
        id="year-typeahead"
        className="picker year-picker"
        placeholder="Year"
        selectHintOnEnter
        multiple
        clearButton
        options={uniq((allModels || []).map(m => m.year))
          .sort((a, b) => b - a)
          .map(y => `${y}`)}
        selected={filteredYears}
        onChange={onFilteredYearsChanged}
      />
      <Typeahead
        id="model-typeahead"
        className="picker model-picker"
        placeholder="Model"
        selectHintOnEnter
        multiple
        clearButton
        options={uniq((allModels || []).map(m => m.model)).sort()}
        selected={filteredModels}
        onChange={onFilteredModelsChanged}
      />
      <ButtonToolbar>
        <ToggleButtonGroup
          type="radio"
          name="allTrimVinFilter"
          value={allTrimVinFilter}
          onChange={value => setAllTrimVinFilter(value)}
        >
          <ToggleButton value={AllTrimVinFilter.ALL}>All</ToggleButton>
          <ToggleButton value={AllTrimVinFilter.TRIM}>Trim</ToggleButton>
          <ToggleButton value={AllTrimVinFilter.VIN}>Vin</ToggleButton>
        </ToggleButtonGroup>
      </ButtonToolbar>
      <ButtonToolbar>
        <ToggleButtonGroup
          type="radio"
          name="enabledFilter"
          value={onlyShowEnabled ? 1 : 0}
          onChange={value =>
            onOnlyShowEnabledChanged && onOnlyShowEnabledChanged(!!value)
          }
        >
          <ToggleButton value={0}>All</ToggleButton>
          <ToggleButton value={1}>Override</ToggleButton>
        </ToggleButtonGroup>
      </ButtonToolbar>
    </div>
  );

  /**
   * Helper function for updating a vin price rule
   *
   * @param update the properties to change in the price rule
   */
  const updateVinPriceRule = (
    vinPriceRule: PriceRule & Partial<CarPriceRule>,
    update: Partial<CarPriceRule> & { enabled?: boolean },
  ) => {
    updatePriceRules(newPriceRules => {
      if (!vinPriceRule.id || !newPriceRules.carRules) {
        if (!vinPriceRule.year || !vinPriceRule.make || !vinPriceRule.model)
          return;
        // if the rule doesn't exist, add it
        const modelEntry: ModelEntry = {
          year: vinPriceRule.year,
          make: vinPriceRule.make,
          model: vinPriceRule.model,
        };
        newPriceRules.carRules = newPriceRules.carRules || [];
        newPriceRules.carRules.push({
          ...vinPriceRule,
          ...update,
          id: generateRuleId(modelEntry, vinPriceRule.trim, vinPriceRule.vin),
          ...modelEntry,
        });
      } else {
        const ruleIndex = newPriceRules.carRules.findIndex(
          r => r.id === vinPriceRule.id,
        );
        if (update.enabled === false) {
          // if the rule is being disabled, delete it
          newPriceRules.carRules.splice(ruleIndex, 1);
        } else {
          // update the existing rule
          newPriceRules.carRules[ruleIndex] = {
            ...newPriceRules.carRules[ruleIndex],
            ...update,
          };
        }
      }
    });
  };

  const renderVinResults = () => (
    <Modal
      isOpen
      // @ts-ignore
      style={MODAL_STYLE}
    >
      <CloseIcon onClick={() => setShowVINAddResults(false)} />
      <div className="modal-message">{vinAddMessage}</div>
    </Modal>
  );

  // eslint-disable-next-line  @typescript-eslint/no-explicit-any
  const addNewVinRule = (decodedCar: any) => {
    const vinPriceRule: PriceRule & Partial<CarPriceRule> = {
      id: undefined,
      year: decodedCar.year,
      make: decodedCar.make,
      model: decodedCar.model,
      trim: decodedCar.trim,
      vin: decodedCar.id,
      priceSource: priceRules.defaultRule.priceSource,
      offsetType: priceRules.defaultRule.offsetType,
    };
    const vehicleData = `[Year: ${decodedCar.year}, Make: ${decodedCar.make}, Model: ${decodedCar.model}] - `;
    let message = `${vehicleData}VIN Added Successfully!`;
    if (priceRules.carRules?.find(r => r.vin === decodedCar.id)) {
      message = `${vehicleData}The VIN is already added`;
    } else if (
      dealer &&
      !dealer.brand?.map(lowerCase).includes(lowerCase(decodedCar.make))
    ) {
      message = `${vehicleData}The make did not match the currently configured makes for the dealership. Please add the make to the dealership first`;
    } else if (
      !allModels?.find(
        model =>
          model.year === decodedCar.year &&
          model.make &&
          decodedCar.make &&
          lowerCase(model.model) === lowerCase(decodedCar.model),
      )
    ) {
      message = `${vehicleData}This vehicle did not fit any year/make/models listed on the page`;
    } else {
      // Add the rule since all is good
      updateVinPriceRule(vinPriceRule, {});
    }
    setVinAddMessage(message);
    setShowVINAddResults(true);
  };

  return (
    <div className="pricing-matrix">
      {errorMessage && <ErrorMessage error={errorMessage} />}
      {showVINAddResults && renderVinResults()}
      {showVINAdd && (
        <VINSearch
          onClose={() => setShowVINAdd(false)}
          onSubmit={addNewVinRule}
          decodedCarCallback={decodedCar => {
            setShowVINAdd(false);
            addNewVinRule(decodedCar);
            return null;
          }}
          buttonText={'ADD'}
        />
      )}
      <div className="row">
        <div className="col-sm-6">
          <Confirm
            onConfirm={() => {
              setPriceRules(
                dealerPriceRules?.data?.[getOppositeClientType(clientType)]?.[
                  condition
                ] ?? getDefaultPriceRulesEntry(),
              );
              updateDealerCashEnabled(
                dealerPriceRules?.data?.[getOppositeClientType(clientType)]?.[
                  condition
                ]?.enableDealerCashPriceOffset ?? false,
              );
              onChange(false);
            }}
            body={`Are you sure you want to copy rules from ${getClientTypeName(
              getOppositeClientType(clientType),
            )}? This will OVERWRITE all rules on this page.`}
            confirmText="Copy"
            title="Copy Rules"
          >
            <Button>
              <i className="glyphicon glyphicon-duplicate" />
              &nbsp; Copy Pricing Rules from{' '}
              {getClientTypeName(getOppositeClientType(clientType))}
            </Button>
          </Confirm>
        </div>
      </div>
      <div className="row">
        <div className="col-sm-12">
          <DefaultRulesTable
            dealer={dealer}
            users={users}
            condition={condition}
            clientType={clientType}
            priceRules={priceRules}
            updatePriceRules={updatePriceRules}
            priceRulesRevisions={priceRulesRevisions}
            dealerCashEnabled={dealerCashEnabled}
            onDealerCashEnabledChange={value => updateDealerCashEnabled(value)}
            selectInventoryRebateEnabled={selectInventoryRebateEnabled}
            onSelectInventoryRebateEnabledChange={value =>
              updateSelectInventoryRebateEnabled(value)
            }
            dealerPriceRules={dealerPriceRules}
          />
        </div>
      </div>
      {!errorMessage &&
        (allModels !== undefined ? (
          <>
            <div className="row">
              <div className="col-sm-10">{renderFilterMenu()}</div>
              <div className="col-sm-2">
                <Button
                  className="pull-right"
                  bsStyle="primary"
                  onClick={() => setShowVINAdd(true)}
                >
                  <i className="glyphicon glyphicon-plus-sign" /> Add Override
                  By VIN
                </Button>
              </div>
            </div>
            {models.map((modelEntry, index) => (
              <div
                className="row"
                // eslint-disable-next-line react/no-array-index-key
                key={`${modelEntry.year}-${modelEntry.model}-${index}`}
              >
                <div className="col-sm-12">
                  <ModelRulesTable
                    dealer={dealer}
                    users={users}
                    modelEntry={modelEntry}
                    condition={condition}
                    clientType={clientType}
                    priceRules={priceRules}
                    updatePriceRules={updatePriceRules}
                    priceRulesRevisions={priceRulesRevisions}
                    dealerCashEnabled={dealerCashEnabled}
                    generateRuleId={generateRuleId}
                    updateVinPriceRule={updateVinPriceRule}
                    allTrimVinFilter={allTrimVinFilter}
                    dealerPriceRules={dealerPriceRules}
                  />
                </div>
              </div>
            ))}
          </>
        ) : (
          <Dimmer active inverted>
            <Loader inverted content="Loading" />
          </Dimmer>
        ))}
      <div className="row shadow-footer">
        <div />
      </div>
      <div className="row save-footer">
        <div className="col-sm-12">
          <Confirm
            onConfirm={onDealerSaved}
            body="Are you sure you want to save changes? They make take up to 3 hours to reflect on your vehicles."
            confirmText="Save"
            title="Save Changes"
            confirmBSStyle="primary"
          >
            <Button bsStyle="primary" disabled={fetching}>
              Save Changes
            </Button>
          </Confirm>
          <Confirm
            onConfirm={() => {
              sendAmplitudeEvent(clientType, condition, Action.REVERT);

              setResetTrigger(resetTrigger + 1);
            }}
            body="Are you sure you want to revert changes?"
            confirmText="Revert"
            title="Revert Changes"
          >
            <Button disabled={fetching}>Revert Changes</Button>
          </Confirm>
        </div>
      </div>
    </div>
  );
};
